import styled from "styled-components";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { Text, Heading, Button, media } from "@otta/design";
import { modularScale, palette, pxToRem } from "@otta/design-tokens";
import * as Banner from "@hire/components/JobListBanner";
import {
  CompanyJobSlotCountsDocument,
  CompanyJobSlotExperimentPhase,
  CompanyWorkflowStatus,
  Feature,
} from "@hire/schema";
import { useAuthorization } from "@hire/providers/authorization";
import { Icon } from "@otta/icons";
import { TooltipWithIcon } from "@hire/components/TooltipWithIcon";

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const Row = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const Separate = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${pxToRem(16)};

  ${media({ from: "tablet" })`
    align-items: center;
    flex-direction: row;
    gap: unset;
  `}
`;

const CountBadge = styled.div.attrs(props => ({
  "data-testid": "count-badge",
  ...props,
}))`
  display: flex;
  padding: 0.5rem;
  justify-content: center;
  border-radius: 0.5rem;
  align-items: center;
  gap: 0.5rem;
`;

const FrozenBadge = styled(CountBadge)`
  background-color: ${palette.extended.blue.shade200};
  max-height: 1.563rem;
`;

const SuccessBadge = styled(CountBadge)`
  background-color: ${palette.extended.green.shade200};
  max-height: 1.563rem;
`;

const IconContainer = styled.div`
  display: flex;
  margin-bottom: ${modularScale(-6)};
`;

const AddNewJobButton = styled(Button)`
  display: flex;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(8)} ${pxToRem(24)};
  width: fit-content;
`;

type JobCountsProps = {
  visibleCount: number;
  companyWorkflowStatus?: CompanyWorkflowStatus;
  hasJobs?: boolean;
};

type SituationalBadgesProps = {
  required: number;
  experimentPhase: CompanyJobSlotExperimentPhase;
  total: number;
  available: number;
  paid: number;
};

function SituationalBanners({ slots }: { slots: SituationalBadgesProps }) {
  if (!slots.paid && slots.required && CompanyJobSlotExperimentPhase.Active) {
    return (
      <Banner.Warning name="job-not-shown" dismissable>
        <Banner.Content>
          <Heading size={2}>It's no longer free to post jobs on Otta</Heading>
          <Text size={1}>
            You're missing out on applications. Publish your jobs on Otta with
            <br />
            our new pay-as-you-go pricing. Pay monthly, cancel anytime.
          </Text>
        </Banner.Content>
      </Banner.Warning>
    );
  } else {
    return null;
  }
}

function SituationalBadges({ slots }: { slots: SituationalBadgesProps }) {
  if (slots.required > 0) {
    if (slots.experimentPhase === CompanyJobSlotExperimentPhase.Active) {
      return (
        <>
          <SuccessBadge>
            <Text size={-1} bold>
              {slots.total} published
            </Text>
          </SuccessBadge>
          <FrozenBadge>
            <IconContainer>
              <Icon icon="snowflake" size={1} />
            </IconContainer>
            <Text bold size={-1}>
              {slots.required} frozen
            </Text>
          </FrozenBadge>
        </>
      );
    } else {
      return null;
    }
  } else if (slots.available > 0) {
    return (
      <>
        <SuccessBadge>
          <Text size={-1} bold>
            {slots.total - slots.available} published
          </Text>
        </SuccessBadge>
        <FrozenBadge>
          <Text size={-1} bold>
            {slots.available} available
          </Text>
        </FrozenBadge>
      </>
    );
  } else {
    return (
      <SuccessBadge>
        <Text size={-1} bold>
          {slots.total} published
        </Text>
      </SuccessBadge>
    );
  }
}

export function JobCounts(props: JobCountsProps) {
  return (
    <CountBadge>
      <Text size={1} bold>
        {props.visibleCount} {props.visibleCount === 1 ? "job" : "jobs"}
      </Text>
    </CountBadge>
  );
}

export function LiveJobCounts(
  props: JobCountsProps & { showPausedModal: () => void }
): React.ReactElement | null {
  const { granted } = useAuthorization(Feature.JobSlots);
  const { granted: createJobGranted, message: createJobMessage } =
    useAuthorization(Feature.CreateJob);
  const { data } = useQuery(CompanyJobSlotCountsDocument, {
    skip: !granted,
  });
  const navigate = useNavigate();
  const slots = data?.currentUser?.currentCompany?.jobSlotData;

  const companyIsLive = [
    CompanyWorkflowStatus.Completed,
    CompanyWorkflowStatus.Changed,
  ].includes(props.companyWorkflowStatus ?? CompanyWorkflowStatus.Completed);

  const companyIsQueuedWithJobs = !companyIsLive && props.hasJobs;

  if (!slots) {
    return <JobCounts {...props} />;
  }

  return (
    <Column>
      <Separate>
        <Row data-testid="badges">
          <JobCounts visibleCount={props.visibleCount} />
          {companyIsLive && <SituationalBadges slots={slots} />}
        </Row>
        {(createJobMessage === "paused" || createJobGranted) && (
          <AddNewJobButton
            level="secondary"
            onClick={() => {
              if (createJobMessage === "paused") {
                props.showPausedModal();
              } else if (
                slots.total === 0 ||
                (slots.available === 0 && slots.paid === 0)
              ) {
                navigate("./add-a-job");
              } else if (slots.available === 0) {
                navigate("./add-job-add-slot");
              } else {
                navigate("./create");
              }
            }}
            disabled={companyIsQueuedWithJobs}
          >
            {!companyIsQueuedWithJobs && <Icon icon="plus" size={-1} />}
            {companyIsQueuedWithJobs && (
              <TooltipWithIcon
                content="You can add more jobs once your account set up is complete. We’ll email you soon."
                icon={<Icon icon="plus" />}
              />
            )}
            <Text bold size={-1}>
              Add new job
            </Text>
          </AddNewJobButton>
        )}
      </Separate>
      <SituationalBanners slots={slots} />
    </Column>
  );
}
